import { useId, useLayoutEffect, useRef, useState, type Ref, type TextareaHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { FieldMessage } from "./input";

export type TextareaProps = {
  label: string;
  hideLabel?: boolean;
  hint?: string;
  error?: string;
  /** Grow with the content up to this many rows. */
  maxRows?: number;
  ref?: Ref<HTMLTextAreaElement>;
} & TextareaHTMLAttributes<HTMLTextAreaElement>;

export function Textarea({
  label,
  hideLabel = false,
  hint,
  error,
  maxRows = 8,
  rows = 3,
  maxLength,
  id,
  className,
  value,
  defaultValue,
  onChange,
  ref,
  ...props
}: TextareaProps) {
  const generated = useId();
  const fieldId = id ?? generated;
  const inner = useRef<HTMLTextAreaElement>(null);
  const [length, setLength] = useState(String(value ?? defaultValue ?? "").length);
  const describedBy = [
    error ? `${fieldId}-error` : undefined,
    !error && hint ? `${fieldId}-hint` : undefined,
    maxLength ? `${fieldId}-count` : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  useLayoutEffect(() => {
    const node = inner.current;
    if (!node) return;
    const style = getComputedStyle(node);
    const line = parseFloat(style.lineHeight) || 20;
    const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const border = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    const max = line * maxRows + padding + border;
    node.style.height = "auto";
    const wanted = node.scrollHeight + border;
    node.style.height = `${Math.min(wanted, max)}px`;
    node.style.overflowY = wanted > max ? "auto" : "hidden";
  });

  return (
    <div className="grid gap-2" data-slot="field">
      <label htmlFor={fieldId} className={cn("text-sm text-foreground", hideLabel && "sr-only")}>
        {label}
      </label>
      <textarea
        ref={(node) => {
          inner.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        }}
        id={fieldId}
        rows={rows}
        maxLength={maxLength}
        value={value}
        defaultValue={defaultValue}
        data-slot="textarea"
        data-field="true"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        onChange={(event) => {
          setLength(event.target.value.length);
          onChange?.(event);
        }}
        className={cn(
          "w-full resize-none rounded-control border border-line bg-surface px-3.5 py-2.5 text-sm leading-5 text-foreground outline-none placeholder:text-muted focus:border-line-strong disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-secondary",
          error && "border-danger focus:border-danger",
          className,
        )}
        {...props}
      />
      <div className="flex items-start justify-between gap-3">
        <FieldMessage id={fieldId} hint={hint} error={error} />
        {maxLength ? (
          <p id={`${fieldId}-count`} className="ml-auto text-xs tabular-nums text-muted" aria-live="polite">
            {length} / {maxLength}
          </p>
        ) : null}
      </div>
    </div>
  );
}
